import { Link, useLocation } from 'react-router-dom';
import { FooterProps } from './types';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ReactComponent as Instagram } from '../../../assets/footer-icons/instagram.svg';
import { ReactComponent as Facebook } from '../../../assets/footer-icons/facebook.svg';
import './styles.scss';
import { connect } from 'react-redux';
import Copyright from './Copyright';
import useGet from '../../../services/useGet';
import { Property } from '../../../types/property';
import { useEffect, useState } from 'react';
import { isEmpty } from '../../../utils/utils';
import { CountryInfo } from '../../../redux/countryInfo/countryInfo';
import { setCountryInfo } from '../../../redux/countryInfo/countryInfo.action';

const transparentLogoDark = process.env.PUBLIC_URL + '/images/logo-dark-transparent.svg';
const noFooterPages = ['/interhome-successful-payment/', '/interhome-retry-payment'];

const Footer = ({ urbainUser, setCountryInfo }: FooterProps) => {
  const { pathname } = useLocation();
  const [showFooter, setShowFooter] = useState(true);
  useEffect(() => {
    setShowFooter(!noFooterPages.some(pageName => pathname.startsWith(pageName)));
  }, [pathname]);

  const [geoData, getGeoData, isLoadingGeo] = useGet<Property>({
    url: `/geo`,
    defaultValue: {}
  });

  useEffect(() => {
    if (!isLoadingGeo) {
      getGeoData();
    }
  }, []);

  useEffect(() => {
    if (geoData && !isEmpty(geoData)) {
      setCountryInfo(geoData);
    }
  }, [geoData]);
  return (
    <>
      {showFooter ? (
        <footer>
          <div className="container desktop-footer d-lg-block d-none">
            <div className="row">
              <div className="col-lg-7 col-md-7 footer-right-border">
                <div className="row">
                  <div className="col-lg-4 col-md-4">
                    <div className="footer-logo">
                      <Link to="/">
                        <LazyLoadImage alt="Logo dark" src={transparentLogoDark} className="img-fluid" />
                      </Link>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-3">
                    <div className="footer-widget">
                      <h3>About</h3>
                      <ul className="list-unstyled">
                        <li>
                          <Link to="/explorers-journal">Explorer's Journal</Link>
                        </li>
                        <li>
                          <Link to="/about-us">About Us</Link>
                        </li>
                        <li>
                          <Link to="/list-your-home-urbain">List your home on Urbain</Link>
                        </li>
                        <li>
                          <a href="https://www.instagram.com/stayurbain" target="_blank">
                            <Instagram />
                          </a>
                          <a href="https://www.facebook.com/stayurbain" target="_blank">
                            <Facebook />
                          </a>
                          <a href="https://www.linkedin.com/company/stay-urbain" target="_blank">
                            <i className="fab fa-linkedin linkedin-icon fa-2x" aria-hidden="true"></i>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-3">
                    <div className="footer-widget">
                      <h3>Support</h3>
                      <ul className="list-unstyled">
                        {urbainUser && (
                          <li className="list-inline-item">
                            <Link to="/profile">My Account</Link>
                          </li>
                        )}
                        <li className="list-inline-item">
                          <Link to="/help-center">Help Center</Link>
                        </li>
                        <li className="list-inline-item">
                          <Link to="/covid">COVID-19 Update</Link>
                        </li>
                        <li>
                          <a href="mailto:hello@stayurbain.com" className="email">
                            hello@stayurbain.com
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="p-5"></div>
                  </div>
                  <div className="col-lg-12 col-md-12 copy-right copy-right-footer">
                    <Copyright />
                  </div>
                </div>
              </div>
              <div className="col-lg-5 col-md-5 footer-right-content-col">
                <div className="footer-right-content">
                  <h3 className="mb-3">Looking to rent out your home in or near the city? Partner with Urbain.</h3>
                  <p>
                    Urbain owners are happy owners. If you have a quality residential property,{' '}
                    <Link to="/list-your-home-urbain" className="__primary-text-color font-weight-bold">
                      click here
                    </Link>{' '}
                    to learn how Urbain can help you maximize your rental income by taking all the hard work and stress off your shoulders.
                  </p>
                  <Link to="/list-your-home-urbain" className="__learn-more-link">
                    Learn More <i className="fas fa-chevron-right"></i>
                  </Link>
                </div>
                <div className="footer-nav text-right">
                  <ul className="list-unstyled m-0">
                    <li className="list-inline-item mr-3">
                      <Link to="/terms-of-use">Terms of Use</Link>
                    </li>
                    <li className="list-inline-item mr-3">
                      <Link to="/privacy-notice">Privacy Notice</Link>
                    </li>
                    <li className="list-inline-item mr-3">
                      <Link to="/cookie-policy">Cookie Policy</Link>
                    </li>
                    {/* <li className="list-inline-item">
                  <Link to="/sitemap">Sitemap</Link>
                </li> Temporary removed from MVP */}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="container mobile-footer d-lg-none">
            <div className="row">
              <div className="col-lg-12">
                <div className="footer-widget">
                  <h3>About</h3>
                  <div className="about-container">
                    <div className="about-item">
                      <Link to="/explorers-journal">Explorer's Journal</Link>
                    </div>
                    <div className="about-item">
                      <Link to="/about-us">About Us</Link>
                    </div>
                    <div className="about-item">
                      <Link to="/list-your-home-urbain">List your home on Urbain</Link>
                    </div>
                  </div>
                </div>
                <div className="footer-widget">
                  <h3>Support</h3>
                  <div className="support-container">
                    {urbainUser && (
                      <div className="support-item">
                        <Link to="/profile">My Account</Link>
                      </div>
                    )}
                    <div className="support-item">
                      <Link to="/help-center">Help Center</Link>
                    </div>
                    <div className="support-item">
                      <Link to="/covid">COVID-19 Update</Link>
                    </div>
                    <div className="support-item">
                      <a href="mailto:hello@stayurbain.com" className="email">
                        hello@stayurbain.com
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mobile-social-nav">
                  <ul className="list-unstyled m-0 p-0">
                    <li className="list-inline-item">
                      <a href="https://www.instagram.com/stayurbain" target="_blank">
                        <Instagram />
                      </a>
                    </li>
                    <li className="list-inline-item">
                      <a href="https://www.facebook.com/stayurbain" target="_blank">
                        <Facebook />
                      </a>
                    </li>
                    <li className="list-inline-item">
                      <a href="https://www.linkedin.com/company/stay-urbain" target="_blank">
                        <i className="fab fa-linkedin linkedin-icon fa-2x" aria-hidden="true"></i>
                      </a>
                    </li>
                  </ul>
                  <Link to="/">
                    <LazyLoadImage alt="Logo dark" src={transparentLogoDark} />
                  </Link>

                  <div className="col-lg-12 col-md-12 copy-right absolute-position">
                    <Copyright />
                  </div>
                </div>
              </div>
            </div>

            <div className="row footer-right-content-col">
              <div className="col-12 footer-right-content">
                <h3 className="mb-3">Looking to rent out your home in or near the city? Partner with Urbain.</h3>
                <p>
                  Urbain owners are happy owners. If you have a quality residential property,{' '}
                  <Link to="/list-your-home-urbain" className="__primary-text-color font-weight-bold">
                    click here
                  </Link>{' '}
                  to learn how Urbain can help you maximize your rental income by taking all the hard work and stress off your shoulders.
                </p>
              </div>
              <div className="footer-nav">
                <div className="nav-container">
                  <div className="nav-item mr-3">
                    <Link to="/terms-of-use">Terms of Use</Link>
                  </div>
                  <div className="nav-item mr-3">
                    <Link to="/privacy-notice">Privacy Notice</Link>
                  </div>
                  <div className="nav-item mr-3">
                    <Link to="/cookie-policy">Cookie Policy</Link>
                  </div>
                  {/* <li className="list-inline-item">
                  <Link to="/sitemap">Sitemap</Link>
                </li> Temporary removed from MVP */}
                </div>
              </div>
            </div>
          </div>
        </footer>
      ) : (
        ''
      )}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  urbainUser: state.user.urbainUser
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    setCountryInfo: (countryInfo: CountryInfo) => dispatch(setCountryInfo(countryInfo))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
